import React, { useEffect, useRef, useState } from "react";
import { Avatar } from "@/types/interfaces";
import { convertKgToLb, convertLbToKg } from "@/Utils";
import TextInputModal from "../TextInputModal";
import { NumberField, seedDigits } from "../TextInputModal/fields";

interface WeightModalProps {
  open: boolean;
  onClose: () => void;
  onCancel: () => void;
  newAvatar: Avatar;
  handlePickerChange: (key: string, value: object) => void;
  unit: string;
  setUnit: React.Dispatch<React.SetStateAction<"lb" | "kg">>;
}

// Mirror the wheel picker's ranges (lb 50–500). kg is bounded to 22–226 — the
// physical equivalent of the lb range (matches the handoff spec) rather than the
// wheel column's looser upper bound.
const LB_MIN = 50;
const LB_MAX = 500;
const KG_MIN = 22;
const KG_MAX = 226;

const clamp = (n: number, min: number, max: number) =>
  Math.min(Math.max(n, min), max);

// The two ranges are not exact mirrors (500 lb is 226.8 kg, 22 kg is 48.5 lb),
// so clamp a converted value into the destination range — otherwise switching
// units at either extreme raises a validation error on a number the user never
// typed.
const lbToKg = (lb: number) => String(clamp(convertLbToKg(lb), KG_MIN, KG_MAX));
const kgToLb = (kg: number) => String(clamp(convertKgToLb(kg), LB_MIN, LB_MAX));

/** The two halves of one conversion, keyed by unit: `{ lb: "140", kg: "64" }`. */
type UnitPair = Record<string, string>;

/**
 * The weight the form row is currently showing, in lb — or null when the user
 * has not set one yet. `newAvatar.weight` is the committed field and is always
 * lb; closeWeightModal runs convertKgToLb before committing a kg entry. The two
 * draft-slot fallbacks cover a draft that has not been committed yet.
 */
const committedLb = (avatar: Avatar): number | null => {
  const committed = parseInt(avatar.weight ?? "", 10);
  if (!isNaN(committed)) return committed;
  const kg = seedDigits(avatar.weightKg);
  if (kg !== "") return convertKgToLb(parseInt(kg, 10));
  const lb = seedDigits(avatar.weightLb);
  return lb === "" ? null : parseInt(lb, 10);
};

/**
 * What the field shows when the modal opens: the draft for the active unit if
 * there is one, otherwise the committed weight converted into that unit.
 *
 * That second branch is what makes a weight entered in kg come back as lb (and
 * vice versa). closeWeightModal clears the *other* unit's draft slot on commit,
 * so on a remount — the form is rehydrated from localStorage every time the
 * user returns from the scan — the active unit's slot is empty even though the
 * user did enter a weight.
 *
 * Returns the counterpart alongside the value whenever it had to convert, so a
 * seeded value round-trips as exactly as a typed one (see `lastPair`).
 */
const seedWeight = (
  avatar: Avatar,
  unit: string,
): { value: string; pair: UnitPair | null } => {
  const own = seedDigits(unit === "kg" ? avatar.weightKg : avatar.weightLb);
  if (own !== "") return { value: own, pair: null };
  const lb = committedLb(avatar);
  if (lb === null) return { value: "", pair: null };
  const asLb = String(clamp(lb, LB_MIN, LB_MAX));
  // The committed field is already lb, so only the kg branch is a conversion.
  if (unit !== "kg") return { value: asLb, pair: null };
  const value = lbToKg(lb);
  return { value, pair: { kg: value, lb: asLb } };
};

const WeightModal: React.FC<WeightModalProps> = ({
  open,
  onClose,
  onCancel,
  newAvatar,
  handlePickerChange,
  unit,
  setUnit,
}) => {
  const [lb, setLb] = useState("");
  const [kg, setKg] = useState("");

  // Both halves of the last conversion, e.g. `{ lb: "140", kg: "64" }`.
  //
  // lb is finer-grained than kg (1 kg ≈ 2.2 lb), so several lb values round to
  // the same kg and converting back can only pick one of them: 140 lb → 64 kg →
  // *141* lb. 55% of the lb range fails that round trip, and no amount of
  // rounding care fixes it — the information is genuinely gone. So instead of
  // recomputing, give back the number the user actually typed, as long as the
  // converted field is still untouched. The equality check is what detects
  // "untouched": edit the kg field and the pair no longer matches, so we convert
  // for real.
  const lastPair = useRef<UnitPair | null>(null);

  // Writing the draft slot is how the modal publishes a value — closeWeightModal
  // reads it, never local state. Keep every path that changes what is on screen
  // (seed, unit switch, typing) paired with the matching write, or Ok commits
  // something the user never saw. See HERE_BE_DRAGONS.md § Avatar form modals.
  const writeDraft = (u: string, value: string) =>
    u === "kg"
      ? handlePickerChange("weightKg", { weightKg: value })
      : handlePickerChange("weightLb", { weightLb: value });

  useEffect(() => {
    if (!open) return;
    const { value, pair } = seedWeight(newAvatar, unit);
    if (unit === "kg") setKg(value);
    else setLb(value);
    writeDraft(unit, value);
    lastPair.current = pair;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Switching units keeps the number the user is looking at: convert what is on
  // screen rather than re-reading the draft, because the other unit's slot holds
  // a stale value (or nothing at all). Deliberately does NOT clear the slot we
  // are switching away from — the Weight row on the form renders `weightKg` /
  // `weightLb` directly, so clearing one would blank the row if the user then
  // cancels.
  const handleUnitChange = (next: string) => {
    if (next === unit) return;
    const current = unit === "kg" ? kg : lb;
    const pair = lastPair.current;
    let converted: string;

    if (pair && pair[unit] === current && pair[next] !== undefined) {
      // Untouched since we produced this pair — hand back the original.
      converted = pair[next];
    } else {
      const n = parseInt(current, 10);
      converted = isNaN(n) ? "" : next === "kg" ? lbToKg(n) : kgToLb(n);
      lastPair.current = isNaN(n)
        ? null
        : { [unit]: current, [next]: converted };
    }

    if (next === "kg") setKg(converted);
    else setLb(converted);
    writeDraft(next, converted);
    setUnit(next as "lb" | "kg");
  };

  const validate = () => {
    if (unit === "kg") {
      const n = parseInt(kg, 10);
      if (!kg || n < KG_MIN || n > KG_MAX) return "Invalid weight input";
      return null;
    }
    const n = parseInt(lb, 10);
    if (!lb || n < LB_MIN || n > LB_MAX) return "Invalid weight input";
    return null;
  };

  return (
    <TextInputModal
      open={open}
      onCancel={onCancel}
      onClose={onClose} // parent's closeWeightModal (reads weightLb or weightKg)
      title="Enter weight"
      validate={validate}
      toggleUnits={{
        value: unit,
        onChange: handleUnitChange,
        options: [
          { value: "lb", label: "lb" },
          { value: "kg", label: "kg" },
        ],
      }}
    >
      {({ hasError, clearError, submit }) =>
        unit === "kg" ? (
          <NumberField
            value={kg}
            hasError={hasError}
            ariaLabel="Weight in kilograms"
            suffix="kg"
            onSubmit={submit}
            onChange={(digits) => {
              setKg(digits);
              handlePickerChange("weightKg", { weightKg: digits });
              clearError();
            }}
          />
        ) : (
          <NumberField
            value={lb}
            hasError={hasError}
            ariaLabel="Weight in pounds"
            suffix="lb"
            onSubmit={submit}
            onChange={(digits) => {
              setLb(digits);
              handlePickerChange("weightLb", { weightLb: digits });
              clearError();
            }}
          />
        )
      }
    </TextInputModal>
  );
};

export default WeightModal;
